import type { NewProductPageQuery, NewShopPageQuery } from '../../../graphql-types';
import type { CentraSelectionItem, Language } from '../../interfaces';
import { getUserData } from './hooks';
import type { GTMEventData, GTMProduct, PageImpression } from './types';

export const sendGTMEvent = (data: GTMEventData | PageImpression) => {
    if (typeof window === 'undefined') return;

    window.dataLayer ||= [];

    window.dataLayer.push({
        ecommerce: null,
    });
    const userData = getUserData();
    const payload = userData ? { ...data, user_data: userData } : data;
    window.dataLayer.push(payload);
};

const extractPrice = (price: string, currency: string) => {
    try {
        if (!price) return 0;
        return parseFloat(price.replace(currency, ''));
    } catch {
        return 0;
    }
};

export const listItemToGTMProduct = (
    item: NonNullable<NewShopPageQuery['allProduct']['nodes'][0]>,
    currency: string,
    language: Language
): GTMProduct => {
    return {
        item_id: item.centraId,
        item_name: item.title[language] || item.title.en || item.title.nb || '',
        item_variant: item.variants.at(0)?.variantId || '',
        item_brand: 'Northern Playground',
        quantity: 1,
        price: extractPrice((item.variants.at(0)?.price ?? '0') as string, currency),
        currency,
    };
};

export const productDetailsToGTMProduct = (
    product: NonNullable<NewProductPageQuery['product']>,
    language: Language,
    currency: string
): GTMProduct => {
    return {
        item_id: product.centraId,
        item_name: product.title[language] ?? '',
        item_brand: 'Northern Playground',
        item_variant: product.variants.at(0)?.variantId || '',
        quantity: 1,
        price: extractPrice((product.variants.at(0)?.price ?? '0') as string, currency),
        currency,
    };
};

const ADYEN_PAYMENT_METHOD_LABELS: Record<string, string> = {
    scheme: 'Kort',
    vipps: 'Vipps',
    klarna: 'Klarna',
    klarna_paynow: 'Klarna',
    klarna_account: 'Klarna',
    paypal: 'PayPal',
    trustly: 'Trustly',
    applepay: 'Apple Pay',
    googlepay: 'Google Pay',
};

export const adyenPaymentMethodToLabel = (rawMethod: string): string =>
    ADYEN_PAYMENT_METHOD_LABELS[rawMethod] || rawMethod;

const SELECTED_PAYMENT_METHOD_STORAGE_KEY = 'npSelectedPaymentMethod';

export const storeSelectedPaymentMethod = (paymentMethod: string) => {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(SELECTED_PAYMENT_METHOD_STORAGE_KEY, paymentMethod);
    } catch {
        // Ignore storage errors (e.g. private browsing)
    }
};

export const consumeSelectedPaymentMethod = (): string | undefined => {
    if (typeof window === 'undefined') return undefined;
    try {
        const value = window.localStorage.getItem(SELECTED_PAYMENT_METHOD_STORAGE_KEY);
        if (value) window.localStorage.removeItem(SELECTED_PAYMENT_METHOD_STORAGE_KEY);
        return value || undefined;
    } catch {
        return undefined;
    }
};

export const selectionItemToGTMProduct = (
    item: CentraSelectionItem,
    currency: string
): GTMProduct => {
    return {
        item_id: item.product.centraProduct,
        item_name: item.product.name,
        item_brand: 'Northern Playground',
        item_variant: item.product.centraVariant,
        item_category: item.product.collectionName,
        quantity: item.quantity,
        price: item.product.priceAsNumber || 0,
        currency,
    };
};
